import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { CtaType } from '@zola-helpers/client/dist/es/tracking/ctaEvents/ctaEvents';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import type { ButtonVariant } from '@zola/zola-ui/src/components/ButtonV3/types';
import { ShareIcon } from '@zola/zola-ui/src/components/SvgIcons';

import cx from 'classnames';

import LinkSharingModal, {
  LinkSharingModalTypes,
} from '~/components/common/modals/LinkSharingModal/LinkSharingModal';
import { useModal } from '~/contexts/ModalContext';
import { CouplesStorefrontDetails } from '~/types/storefrontDetails';
import { getImageUrl } from '~/util/imageUtils';
import { getAbsoluteUrl } from '~/util/seoUtils';
import { trackCtaClicked, TrackingProductLocation } from '~/util/trackingHelper';

// styling
import styles from './ShareButton.module.less';

export interface ShareButtonProps {
  uuid: string;
  slug?: string;
  className?: string;
  variant?: 'full' | 'compact';
  modalHed?: string;
  trackingLocation?: TrackingProductLocation;
  businessUnit?: string;
  customCtaId?: string;
  buttonClassName?: string;
  modalType?: LinkSharingModalTypes;
  imageUuid?: string;
  details?: CouplesStorefrontDetails;
  name?: string;
  location?: string;
  taxonomyKey?: VendorTaxonomyKey;
  buttonV3Variant?: ButtonVariant;
}

const ShareButton = ({
  uuid,
  slug,
  className,
  trackingLocation,
  variant = 'full',
  modalHed = 'Share this photo',
  businessUnit,
  customCtaId,
  buttonClassName,
  modalType = 'real-weddings',
  imageUuid,
  name,
  location,
  taxonomyKey,
  buttonV3Variant = 'transparent',
}: ShareButtonProps) => {
  const { showCustomModal } = useModal();
  const isFull = variant === 'full';

  const shareUrlObj: Record<LinkSharingModalTypes, string> = {
    'real-weddings': getAbsoluteUrl(`/inspiration/photo/${slug}`),
    vendors: getAbsoluteUrl(`/wedding-vendors/${taxonomyKey}/${slug}`),
  };
  const shareUrl = shareUrlObj[modalType];
  const shareStorefrontObj =
    modalType === 'vendors'
      ? {
          name,
          location,
          taxonomyKey,
        }
      : {};

  const onButtonClick = () => {
    // Tracking event: "CTA Clicked - Share Button"
    const trackingProps = {
      businessUnit,
      ctaType: CtaType.BUTTON,
      location: trackingLocation,
      ctaId: customCtaId,
      groupId: 'SHARE',
      ...(modalType === 'vendors' ? { storefrontUuid: uuid } : { weddingPhotoUuid: uuid }),
    };
    trackCtaClicked(trackingProps);

    showCustomModal(
      LinkSharingModal,
      {
        trackingProps,
        hed: modalHed,
        image: getImageUrl(imageUuid || uuid, { width: 300, height: 300, quality: 60 }),
        url: shareUrl,
        modalType,
        ...shareStorefrontObj,
      },
      {
        className: 'marketplace__link-sharing-modal-container',
        size: 'lg',
        mobileHalfSheet: true,
      }
    );
  };

  return (
    <div className={cx(styles.shareButtonContainer, className)}>
      <ButtonV3
        size="small"
        variant={buttonV3Variant}
        bordered={false}
        onClick={onButtonClick}
        className={cx(
          buttonClassName,
          !isFull && styles.shareButtonCompact,
          buttonV3Variant === 'transparent' && styles.transparentButton
        )}
        fullWidth={isFull}
      >
        <ShareIcon width={20} height={20} className={cx({ [styles.icon]: isFull })} />
        {isFull && 'Share'}
      </ButtonV3>
    </div>
  );
};

export default ShareButton;
